import authService from './authService'
import { triggerBlobDownload } from '@/utils/downloadFile'

/**
 * Case Service for Olvidadme
 *
 * Implementing Service Layer pattern from PATTERNS_FROM_BASKET_BASE.md
 * Connects with enterprise-ready backend case system (7 endpoints)
 *
 * Features:
 * - Full CRUD operations for legal cases
 * - Document upload and management
 * - Timeline tracking for GDPR compliance
 * - Service tier validation
 * - Spanish error message handling
 * - Automatic token management via auth interceptors (uses apiClient)
 *
 * ARCHITECTURE NOTE:
 * Uses apiClient from authService which has built-in:
 * - Token refresh on 401
 * - CSRF token management
 * - Consistent error handling
 */

// ==========================================
// AXIOS CLIENT CONFIGURATION
// ==========================================

// Use apiClient from authService for automatic token refresh handling
const { apiClient } = authService

// Cases endpoint base path
const CASES_PATH = '/cases'

// NOTE: No custom interceptors needed - apiClient from authService already handles:
// - CSRF tokens for mutating requests
// - 401 token refresh
// - Rate limiting
// - Consistent error handling

// ==========================================
// CASE SERVICE METHODS
// ==========================================

const caseService = {
  /**
   * Get cases with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.status - Filter by status
   * @param {string} params.type - Filter by type
   * @param {string} params.serviceTier - Filter by service tier
   * @param {string} params.search - Search query
   * @param {string} params.dateFrom - Start date filter
   * @param {string} params.dateTo - End date filter
   * @returns {Promise<Object>} - Cases response
   */
  async getCases(params = {}) {
    try {
      const response = await apiClient.get(CASES_PATH, { params })
      return response.data
    } catch (error) {
      console.error('Case service: Failed to fetch cases', error)
      throw error
    }
  },

  /**
   * Get single case by ID
   * 
   * @param {string} caseId - Case ID
   * @returns {Promise<Object>} - Case data
   */
  async getCase(caseId) {
    try {
      const response = await apiClient.get(`${CASES_PATH}/${caseId}`)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to fetch case', { caseId, error })
      throw error
    }
  },

  /**
   * Create new case
   *
   * @param {Object} caseData - Case creation data
   * @param {string} caseData.type - Case type (olvido, olvido_postmortem, testamento)
   * @param {string} caseData.serviceTier - Service tier (olvido, olvido_postmortem, testamento)
   * @param {Object} caseData.clientInfo - Client information
   * @param {Object} caseData.deceasedInfo - Deceased information (for dual form)
   * @param {Array} caseData.urls - URLs for removal (olvido, olvido_postmortem)
   * @param {Object} caseData.details - Additional case details
   * @returns {Promise<Object>} - Created case
   */
  async createCase(caseData) {
    try {
      // Validate required fields based on case type
      if (caseData.serviceTier === 'olvido_postmortem' && !caseData.deceasedInfo) {
        throw new Error('Información del fallecido es requerida para casos post-mortem')
      }

      // URLs are required for olvido and olvido_postmortem services
      const requiresUrls = caseData.serviceTier === 'olvido' || caseData.serviceTier === 'olvido_postmortem'
      if (requiresUrls && (!caseData.urls || caseData.urls.length === 0)) {
        throw new Error('Debe proporcionar al menos una URL para el derecho al olvido')
      }

      const response = await apiClient.post(CASES_PATH, caseData)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to create case', { type: caseData.type, error })
      throw error
    }
  },

  /**
   * Update existing case
   * 
   * @param {string} caseId - Case ID
   * @param {Object} updates - Update data
   * @returns {Promise<Object>} - Updated case
   */
  async updateCase(caseId, updates) {
    try {
      const response = await apiClient.put(`${CASES_PATH}/${caseId}`, updates)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to update case', { caseId, error })
      throw error
    }
  },

  /**
   * Delete/Cancel case
   * 
   * @param {string} caseId - Case ID
   * @param {Object} data - Cancellation data
   * @param {string} data.reason - Cancellation reason
   * @returns {Promise<Object>} - Deletion response
   */
  async deleteCase(caseId, data = {}) {
    try {
      const response = await apiClient.delete(`${CASES_PATH}/${caseId}`, { data })
      return response.data
    } catch (error) {
      console.error('Case service: Failed to delete case', { caseId, error })
      throw error
    }
  },

  /**
   * Get case timeline
   * 
   * @param {string} caseId - Case ID
   * @returns {Promise<Object>} - Timeline events
   */
  async getCaseTimeline(caseId) {
    try {
      const response = await apiClient.get(`${CASES_PATH}/${caseId}/timeline`)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to fetch timeline', { caseId, error })
      throw error
    }
  },

  /**
   * Add timeline event
   * 
   * @param {string} caseId - Case ID
   * @param {Object} eventData - Timeline event data
   * @param {string} eventData.type - Event type
   * @param {string} eventData.description - Event description
   * @param {Object} eventData.metadata - Additional metadata
   * @returns {Promise<Object>} - Created event
   */
  async addTimelineEvent(caseId, eventData) {
    try {
      const response = await apiClient.post(`${CASES_PATH}/${caseId}/timeline`, eventData)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to add timeline event', { caseId, error })
      throw error
    }
  },

  /**
   * Get case documents
   * 
   * @param {string} caseId - Case ID
   * @returns {Promise<Object>} - Case documents
   */
  async getCaseDocuments(caseId) {
    try {
      const response = await apiClient.get(`${CASES_PATH}/${caseId}/documents`)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to fetch documents', { caseId, error })
      throw error
    }
  },

  /**
   * Upload document to case
   * 
   * @param {string} caseId - Case ID
   * @param {FormData} formData - Document form data
   * @param {Function} onProgress - Upload progress callback
   * @returns {Promise<Object>} - Upload response
   */
  async uploadDocument(caseId, formData, onProgress = null) {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
      
      // Add progress callback if provided
      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      }
      
      const response = await apiClient.post(`${CASES_PATH}/${caseId}/documents`, formData, config)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to upload document', { caseId, error })
      
      // Handle specific upload errors
      if (error.response?.data?.message?.includes('size')) {
        error.message = 'El archivo es demasiado grande. Tamaño máximo: 10MB'
      } else if (error.response?.data?.message?.includes('type')) {
        error.message = 'Tipo de archivo no permitido. Solo se permiten PDF, JPG, PNG'
      }
      
      throw error
    }
  },

  /**
   * Delete document from case
   * 
   * @param {string} caseId - Case ID
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} - Deletion response
   */
  async deleteDocument(caseId, documentId) {
    try {
      const response = await apiClient.delete(`${CASES_PATH}/${caseId}/documents/${documentId}`)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to delete document', { caseId, documentId, error })
      throw error
    }
  },

  /**
   * Get case statistics for dashboard
   * 
   * @returns {Promise<Object>} - Case statistics
   */
  async getCaseStatistics() {
    try {
      const response = await apiClient.get(`${CASES_PATH}/statistics`)
      return response.data
    } catch (error) {
      console.error('Case service: Failed to fetch statistics', error)
      throw error
    }
  },

  /**
   * Export cases to CSV/PDF
   * 
   * @param {Object} params - Export parameters
   * @param {string} params.format - Export format (csv, pdf)
   * @param {Object} params.filters - Filter criteria
   * @returns {Promise<Blob>} - Export file
   */
  async exportCases(params = {}) {
    try {
      const response = await apiClient.get(`${CASES_PATH}/export`, {
        params,
        responseType: 'blob'
      })
      
      // Trigger download
      const filename = `casos_${new Date().toISOString().split('T')[0]}.${params.format || 'csv'}`
      triggerBlobDownload(response.data, filename)
      
      return response.data
    } catch (error) {
      console.error('Case service: Failed to export cases', error)
      throw error
    }
  },

  /**
   * Helper interno para descargar templates PDF
   * @private
   * @param {string} caseId - ID del caso
   * @param {string} templateType - Tipo de template (ej: 'authorization-template')
   * @param {string} filename - Nombre del archivo descargado
   * @returns {Promise<Blob>} - PDF blob
   */
  async _downloadPdfTemplate(caseId, templateType, filename) {
    try {
      const response = await apiClient.get(`${CASES_PATH}/${caseId}/${templateType}`, {
        responseType: 'blob'
      })

      triggerBlobDownload(response.data, filename)

      return response.data
    } catch (error) {
      console.error(`Case service: Failed to download ${templateType}`, { caseId, error })
      throw error
    }
  },

  /**
   * Descargar plantilla de autorización en PDF
   * @param {string} caseId - ID del caso
   * @returns {Promise<Blob>} - PDF blob
   */
  async getAuthorizationTemplate(caseId) {
    return this._downloadPdfTemplate(caseId, 'authorization-template', 'autorizacion-olvidadme.pdf')
  },

  /**
   * Descargar template de mandato de herederos (solo para casos post-mortem)
   * Este documento se usa cuando hay múltiples herederos y uno actúa en nombre de los demás
   * @param {string} caseId - ID del caso
   * @returns {Promise<Blob>} - PDF blob
   */
  async getHeirMandateTemplate(caseId) {
    return this._downloadPdfTemplate(caseId, 'heir-mandate-template', 'mandato-herederos-olvidadme.pdf')
  },

  /**
   * Firmar autorización digitalmente
   * @param {string} caseId - ID del caso
   * @param {string} signatureDataURL - Firma en formato data URL (base64)
   * @returns {Promise<Object>} - Respuesta con estado de documentación actualizado
   */
  async signAuthorization(caseId, signatureDataURL) {
    try {
      const response = await apiClient.post(`${CASES_PATH}/${caseId}/sign-authorization`, {
        signatureDataURL
      })
      return response.data
    } catch (error) {
      console.error('Case service: Failed to sign authorization', { caseId, error })
      throw error
    }
  },

  /**
   * Get available platforms for "derecho al olvido"
   * 
   * @returns {Promise<Array>} - Available platforms
   */
  async getAvailablePlatforms() {
    try {
      const response = await apiClient.get(`${CASES_PATH}/platforms`)
      return response.data.platforms
    } catch (error) {
      console.error('Case service: Failed to fetch platforms', error)
      throw error
    }
  },

  /**
   * Get user cases with tier-aware filtering
   * Implementing Tier-Aware Service Pattern from PATTERNS_FROM_BASKET_BASE.md
   * Following ADR-098: Case Service Tier-Aware Implementation
   *
   * Integrates with backend getCasesForUserWithTier() method to provide
   * tier-specific case data filtering for dashboard optimization
   *
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} - Tier-aware cases response
   */
  async getUserCases(params = {}) {
    try {
      // Call the standard getCases method which will automatically
      // apply tier-aware filtering via backend authentication
      const response = await this.getCases(params)

      return {
        cases: response.data?.cases || response.cases || [],
        pagination: response.data?.pagination || response.pagination || {},
        tier: response.tier || 'olvido'
      }
    } catch (error) {
      console.error('Case service: Failed to fetch user cases', error)
      throw error
    }
  },

  /**
   * Validate case data before submission
   *
   * @param {Object} caseData - Case data to validate
   * @returns {Object} - Validation result
   */
  validateCaseData(caseData) {
    const errors = {}

    // Common validations
    if (!caseData.type) {
      errors.type = 'El tipo de caso es requerido'
    }

    if (!caseData.serviceTier) {
      errors.serviceTier = 'El nivel de servicio es requerido'
    }
    
    if (!caseData.clientInfo?.name) {
      errors['clientInfo.name'] = 'El nombre del cliente es requerido'
    }
    
    if (!caseData.clientInfo?.email) {
      errors['clientInfo.email'] = 'El email es requerido'
    }
    
    // Service-specific validations for URLs
    const requiresUrls = caseData.serviceTier === 'olvido' || caseData.serviceTier === 'olvido_postmortem'
    if (requiresUrls) {
      if (!caseData.urls || caseData.urls.length === 0) {
        errors.urls = 'Debe proporcionar al menos una URL'
      }

      // Validate URL limits by service type (5 URLs for olvido and olvido_postmortem, 0 for testamento)
      const urlLimits = {
        olvido: 5,
        olvido_postmortem: 5,
        testamento: 0
      }

      const limit = urlLimits[caseData.serviceTier] || 5
      if (caseData.urls && caseData.urls.length > limit) {
        errors.urls = `El servicio ${caseData.serviceTier} permite hasta ${limit} URLs`
      }
    }

    // Post-mortem specific validations
    if (caseData.serviceTier === 'olvido_postmortem') {
      if (!caseData.deceasedInfo?.name) {
        errors['deceasedInfo.name'] = 'El nombre del fallecido es requerido'
      }

      if (!caseData.deceasedInfo?.deathDate) {
        errors['deceasedInfo.deathDate'] = 'La fecha de fallecimiento es requerida'
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  },

  /**
   * Upload/link additional document to case
   * Sprint Documentación - Fase 2: Additional Documents
   *
   * @param {string} caseId - Case ID
   * @param {Object} params - { docId: string, documentId: string }
   * @returns {Promise<Object>} Updated additional documents data
   */
  async uploadAdditionalDocument(caseId, { docId, documentId }) {
    const response = await apiClient.put(
      `${CASES_PATH}/${caseId}/additional-documents`,
      { docId, documentId }
    )
    return response.data.data
  },

  async fetchQuestionnaire(caseId) {
    const response = await apiClient.get(`${CASES_PATH}/${caseId}/questionnaire`)
    return response.data.data
  },

  async saveQuestionnaireStep(caseId, stepIndex, stepData) {
    const response = await apiClient.patch(`${CASES_PATH}/${caseId}/questionnaire/step`, {
      step: stepIndex,
      data: stepData
    })
    return response.data.data
  },

  async completeQuestionnaire(caseId) {
    const response = await apiClient.post(`${CASES_PATH}/${caseId}/questionnaire/complete`)
    return response.data.data
  },

  /**
   * Download Testamento Digital as PDF
   * Triggers browser download of the testament PDF document
   *
   * @param {string} caseId - Case ID of a testamento case
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async downloadTestamentPdf(caseId) {
    try {
      const response = await apiClient.get(`${CASES_PATH}/${caseId}/testament-pdf`, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const contentDisposition = response.headers['content-disposition']
      let filename = `testamento-digital-${caseId}.pdf`
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/)
        if (match) filename = match[1]
      }
      triggerBlobDownload(blob, filename)

      return { success: true }
    } catch (error) {
      let userMessage = 'Error al descargar el testamento digital.'
      if (error.response?.status === 404) {
        userMessage = 'Caso no encontrado.'
      } else if (error.response?.status === 400) {
        userMessage = 'El cuestionario aún no ha sido iniciado. Completa al menos una sección primero.'
      } else if (error.response?.status === 401) {
        userMessage = 'Sesión expirada. Por favor, inicia sesión de nuevo.'
      }
      return { success: false, error: userMessage }
    }
  }
}

export default caseService